import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { knowledgeBaseAPI } from '../../../api/services';
import { KnowledgeBaseDoc } from '../../../types';

interface Props {
  projectId: string;
}

const VISIBILITY_COLORS: Record<string, string> = {
  private: 'bg-gray-100 text-gray-600',
  department: 'bg-blue-100 text-blue-700',
  team: 'bg-purple-100 text-purple-700',
  public: 'bg-green-100 text-green-700',
};

function timeAgo(date: string): string {
  const diff = Date.now() - new Date(date).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  return new Date(date).toLocaleDateString();
}

const ProjectKnowledgeBase: React.FC<Props> = ({ projectId }) => {
  const navigate = useNavigate();
  const [docs, setDocs] = useState<KnowledgeBaseDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    let mounted = true;
    knowledgeBaseAPI.getDocsByProject(projectId)
      .then((data) => { if (mounted) setDocs(Array.isArray(data) ? data : []); })
      .catch(() => {})
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [projectId]);

  return (
    <div className="mt-8">
      {/* Section header */}
      <button
        className="w-full flex items-center justify-between mb-3"
        onClick={() => setExpanded((v) => !v)}
      >
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Knowledge Base
          </span>
          {docs.length > 0 && (
            <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full">{docs.length}</span>
          )}
        </div>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {expanded && (
        <div className="space-y-2">
          {loading ? (
            <div className="flex items-center gap-2 py-3 text-sm text-gray-400">
              <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
              Loading documents…
            </div>
          ) : docs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-6 text-center border border-dashed border-gray-200 rounded-xl">
              <p className="text-sm text-gray-400 mb-2">No knowledge base documents linked to this project.</p>
              <button
                onClick={() => navigate('/knowledge-base/new', { state: { linkedProjectId: projectId } })}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
              >
                + Create a document for this project
              </button>
            </div>
          ) : (
            <>
              {docs.map((doc) => (
                <div
                  key={doc._id}
                  onClick={() => navigate(`/knowledge-base/${doc._id}`)}
                  className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 cursor-pointer transition-colors group"
                >
                  <span className="text-lg flex-shrink-0 mt-0.5">{doc.icon || '📄'}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 group-hover:text-blue-700 truncate">
                      {doc.title || 'Untitled'}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${VISIBILITY_COLORS[doc.visibility] ?? 'bg-gray-100 text-gray-600'}`}>
                        {doc.visibility}
                      </span>
                      <span className="text-xs text-gray-400">{timeAgo(doc.updatedAt)}</span>
                      {(doc.tags ?? []).slice(0, 2).map((t) => (
                        <span key={t} className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">{t}</span>
                      ))}
                    </div>
                  </div>
                  <svg className="w-4 h-4 text-gray-300 group-hover:text-blue-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              ))}
              <button
                onClick={() => navigate('/knowledge-base/new', { state: { linkedProjectId: projectId } })}
                className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-blue-600 transition-colors pt-1"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add document
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ProjectKnowledgeBase;
